import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Cable, Recycle, Sparkles, Leaf, Settings } from 'lucide-react';

// Custom Separation Icon Component
const SeparationIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    {/* Left arrow */}
    <path d="M7 12H2L6 8" />
    <path d="M2 12L6 16" />
    {/* Right arrow */}
    <path d="M17 12H22L18 8" />
    <path d="M22 12L18 16" />
    {/* Center vertical line */}
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

export const Processo = () => {
  const steps = [
    {
      icon: Cable,
      title: 'Raccolta Cavi',
      description:
        'Raccogliamo cavi elettrici di diverse tipologie e dimensioni, provenienti da demolizioni, scarti industriali e materiale di recupero.',
    },
    {
      icon: SeparationIcon,
      title: 'Separazione',
      description:
        'Attraverso il nostro processo produttivo avanzato, separiamo meccanicamente il rame dalla guaina isolante del cavo.',
    },
    {
      icon: Settings,
      title: 'Granulazione',
      description:
        'Il materiale separato viene ridotto in granulato: granulato di rame purissimo e granulato di materiali polimerici (PVC, PE, gomma).',
    },
    {
      icon: Recycle,
      title: 'Valorizzazione',
      description:
        'I materiali recuperati vengono selezionati, certificati e reintrodotti nei cicli produttivi come materie prime seconde di qualità.',
    },
  ];

  const galleryImages = [
    {
      title: 'Cavi Elettrici in Ingresso',
      placeholder: 'Immagine dei cavi elettrici da processare',
      imageUrl: 'https://customer-assets.emergentagent.com/job_ecocopper/artifacts/8mhxy4x5_image.png',
    },
    {
      title: 'Granulato di Rame',
      placeholder: 'Immagine del granulato di rame ottenuto',
      imageUrl: 'https://customer-assets.emergentagent.com/job_copper-recovery/artifacts/afnyyv08_FOTO%20RAME%20MACINATO.jpeg',
    },
    {
      title: 'Granulato di Plastica',
      placeholder: 'Immagine del granulato di plastica recuperato',
      imageUrl: 'https://customer-assets.emergentagent.com/job_copper-recovery/artifacts/j39ir425_FOTO%20POLIMERI.jpeg',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section Premium */}
      <section className="relative py-24 bg-gradient-to-br from-[var(--verdigris)] to-[var(--verdigris-dark)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        </div>
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Leaf className="h-4 w-4" />
              <span className="text-sm font-semibold">Tecnologia Sostenibile</span>
            </div>
            <h1 className="text-6xl font-bold mb-6 animate-fade-in">Processo Produttivo</h1>
            <p className="text-xl text-white/90 animate-fade-in leading-relaxed" style={{ animationDelay: '0.1s' }}>
              Tecnologia e competenza al servizio dell'economia circolare
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-8">Un Processo Efficiente e Sostenibile</h2>
            <p className="text-xl text-[var(--text-light)] leading-relaxed">
              Il nostro processo produttivo ci permette di separare efficacemente il rame dalla guaina dei cavi elettrici,
              ottenendo materiali di alta qualità pronti per essere reintrodotti nei cicli produttivi. Ogni fase è ottimizzata
              per massimizzare il recupero e minimizzare l'impatto ambientale.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Le Fasi del Processo</h2>
            <p className="text-xl text-[var(--text-light)] leading-relaxed">
              Dal cavo elettrico ai materiali recuperati in 4 step
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border-2 border-gray-200 hover:border-[var(--copper-primary)] transition-all duration-500 hover:shadow-xl hover:-translate-y-2 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    <div className="text-5xl font-extrabold text-[var(--copper-primary)] mb-4">
                      {(index + 1).toString().padStart(2, '0')}
                    </div>
                    <h3 className="text-xl font-bold mb-4 group-hover:text-[var(--copper-primary)] transition-colors">{step.title}</h3>
                    <p className="text-[var(--text-light)] leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-gradient-to-b from-[var(--warm-gray)] to-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Galleria Processo Produttivo</h2>
            <p className="text-xl text-[var(--text-light)] leading-relaxed">
              Uno sguardo ai materiali che trattiamo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {galleryImages.map((image, index) => (
              <Card
                key={index}
                className="overflow-hidden border-2 border-gray-200 hover:border-[var(--verdigris)] transition-all duration-500 hover:shadow-2xl group hover:-translate-y-2"
              >
                <CardContent className="p-0">
                  {image.imageUrl ? (
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={image.imageUrl}
                        alt={image.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-square bg-gradient-to-br from-[var(--warm-gray)] to-[#e8f5f2] flex items-center justify-center relative overflow-hidden">
                      <div className="text-center p-8">
                        <div className="text-6xl mb-4 group-hover:scale-110 transition-transform duration-300">
                          📷
                        </div>
                        <p className="text-[var(--text-light)] font-medium">{image.placeholder}</p>
                      </div>
                    </div>
                  )}
                  <div className="p-6 bg-white">
                    <h3 className="font-semibold text-lg text-center group-hover:text-[var(--verdigris)] transition-colors">{image.title}</h3>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-24 bg-gradient-to-br from-[var(--copper-primary)] to-[var(--copper-dark)] text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-10"></div>
        <div className="container relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-8">Materiali Recuperati</h2>
              <p className="text-xl text-white/90 leading-relaxed">
                Produciamo materiali di alta qualità pronti per nuovi utilizzi
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <h3 className="text-3xl font-bold mb-6">Granulato di Rame</h3>
                <p className="text-white/90 leading-relaxed mb-6 text-lg">
                  Rame di alta qualità pronto per essere rifuso e utilizzato nell'industria elettrica, elettronica e manifatturiera.
                  Il nostro processo garantisce una <strong>purezza certificata del 99%</strong> e una qualità costante del materiale recuperato.
                </p>
                <div className="inline-block bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3">
                  <span className="text-white font-bold text-lg">✓ Purezza 99%</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-10 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <h3 className="text-3xl font-bold mb-6">Materiali Polimerici</h3>
                <p className="text-white/90 leading-relaxed text-lg">
                  Granulato composto da plastiche (PVC, PE) e gomma, separato e selezionato per essere riutilizzato
                  in nuove applicazioni industriali. Contribuiamo così a ridurre la dipendenza da materie prime vergini.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Authorization Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[var(--warm-gray)]">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-br from-[var(--warm-gray)] to-white rounded-3xl p-16 border-2 border-gray-200 shadow-xl">
              <div className="text-7xl mb-8">📄</div>
              <h2 className="text-4xl font-bold mb-6">Autorizzazione R13</h2>
              <p className="text-xl text-[var(--text-light)] leading-relaxed">
                Operiamo con <strong className="text-[var(--copper-primary)]">autorizzazione R13 "messa in riserva"</strong>,
                nel pieno rispetto delle normative ambientali vigenti. Questo ci permette di gestire i materiali
                in modo sicuro e conforme, garantendo trasparenza e professionalità in ogni fase del processo.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};